'use client';

import PropTypes from 'prop-types';
import {useCallback, useState} from 'react';
import urls from 'urls';
import {Button} from '../../atoms/Button';
import {Divider} from '../../atoms/Divider';
import {InputText} from '../../atoms/InputText';
import {Link} from '../../atoms/Link';
import {Overlay} from '../../atoms/Overlay';
import {Typography} from '../../atoms/Typography';
import {useIsLargeAndUp} from '../../hooks/useMatchQuery';
import {tanzanite} from '../../sass/base/_exports.sass';
import {CloseLineIcon} from '../../svg/CloseLineIcon';
import {saveUserDataToLocalStorage, submitLoginWithCaptcha} from '../../utils/auth/utils';
import {validateEmail} from '../../utils/validateEmail';
import './LoginContainer.sass';

const trackUserId = (userId, subscriptionPlanName, email) => {
  // biome-ignore lint/style/useNamingConvention: Fix me!
  window.dataLayer.push({user_identifier: userId});
  // biome-ignore lint/style/useNamingConvention: Fix me!
  window.dataLayer.push({is_logged_in: 1});
  // biome-ignore lint/style/useNamingConvention: Fix me!
  if (subscriptionPlanName) window.dataLayer.push({subscription_plan_name: subscriptionPlanName});
  window.dataLayer.push({email});
};

export const LoginContainer = ({
  forgotPasswordMode,
  isModal,
  handlePwReset,
  onCreateAccountClick,
  onForgotPasswordClick,
  onForgotPasswordCancel,
  onAppleLoginClick,
  onGoogleLoginClick,
  onCloseModal,
  onSuccess,
}) => {
  const isLargeAndUp = useIsLargeAndUp();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginFailure, setLoginFailure] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [forgotPasswordSentModalOpen, setForgotPasswordSentModalOpen] = useState(false);

  const params = new URLSearchParams(window.location.search);
  const complete = params.get('complete');
  const nextParam = params.get('next')?.replace(/\/+$/, '');
  const proSignUp = nextParam === '/checkout/pro-subscription';
  const showHostMessage = ['event', 'invite_more', 'view_host'].includes(complete);

  const handleLogin = useCallback(
    async (e) => {
      e.preventDefault();
      setEmailError('');
      const {valid} = validateEmail(email, false);

      if (!valid || password.length < 6) {
        setLoginFailure(true);
      } else {
        try {
          // Attempt login
          const resp = await submitLoginWithCaptcha({email, password});
          if (resp?.data) {
            // login success
            const {data} = resp;
            const userId = data.user_id;
            setLoginFailure(false);
            trackUserId(userId, data.subscription_plan_name, data.email);
            saveUserDataToLocalStorage(data);
            onSuccess(
              userId,
              data.full_name,
              data.subscription_plan_name,
              data.email,
              data.avatar_disk,
              data.image_url,
              data.initials,
              data.first_name
            );
          }
        } catch (err) {
          localStorage.removeItem('userData');
          const errorMsg = err?.xhr?.responseText;
          if (errorMsg === 'Password update required') {
            handlePwReset(true);
          } else if (err?.xhr?.status === 403) {
            // biome-ignore lint/suspicious/noAlert: Fix me!
            alert('There was an error with your request - please reload and try again.');
          } else {
            setLoginFailure(true);
          }
        }
      }
    },
    [email, password, onSuccess, handlePwReset]
  );

  const handleForgotPassword = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      $.ajax({
        url: '/modal/forgot',
        data: {
          // biome-ignore lint/style/useNamingConvention: Fix me!
          email_forgot: email,
        },
        dataType: 'json',
        type: 'POST',
        success: () => {
          if (window.mobile) {
            // biome-ignore lint/suspicious/noAlert: Fix me!
            alert(
              `If an account matching ${email} is found, you should receive an email with instructions for resetting your password shortly.`
            );
            window.location = '/';
          } else {
            setForgotPasswordSentModalOpen(true);
          }
        },
        error: (xhr) => {
          try {
            if (xhr.status === 429) {
              setEmailError('Too many requests, try again later.');
              return;
            }
            const response = JSON.parse(xhr.responseText);
            setEmailError(response.__all__[0]);
          } catch (err) {
            // TODO: Show some sort of error message to the user?
            evite?.error(err);
          }
        },
      });
    },
    [email]
  );

  const hideForgotPasswordSentModal = useCallback(() => {
    setForgotPasswordSentModalOpen(false);
  }, []);

  return (
    <div className="meganav-login-modal">
      <div className="logo-header">
        {!proSignUp && !isModal && (
          // biome-ignore lint/a11y/useAnchorContent: existing functionality
          <a
            id="logo_top"
            href="/"
            className="logo evite-logo"
            data-qa-id="evite-logo"
            aria-label="evite home"
          />
        )}
        {!proSignUp && (
          <Typography variant="header7">
            {forgotPasswordMode ? 'Forgot Your Password?' : 'Sign In'}
          </Typography>
        )}
        {isModal && (
          <button
            type="button"
            className="unstyled-button"
            onClick={onCloseModal}
            data-qa-id="signupModalCloseButton"
            aria-label="close login"
          >
            <CloseLineIcon />
          </button>
        )}
      </div>
      {!isModal && showHostMessage && !forgotPasswordMode && (
        <>
          <Typography variant="header3">Are you a guest of this event?</Typography>
          <Typography variant="paragraph3">
            {/** biome-ignore lint/style/useNamingConvention: Fix me! */}
            <Link data-qa-id="sign-in-link" href={urls.get('view', {event_id: window.event_id})}>
              Click here
            </Link>{' '}
            to view &ldquo;{window.event_title}&rdquo;.
          </Typography>
          <Divider />
          <Typography variant="header3">Are you the host?</Typography>
          <Typography variant="paragraph3">
            Sign in to your Evite account to manage your event!
          </Typography>
        </>
      )}

      <div className="modal-body">
        {!forgotPasswordMode ? (
          <>
            {(!isLargeAndUp || isModal) && !proSignUp ? (
              <Typography as="h2" variant="header4" data-qa-id="loginModalSignInHeaderText">
                Welcome back! Sign in to your account
              </Typography>
            ) : null}
            {proSignUp && (
              <div className="modal-header__pro-signup">
                <Typography
                  as="h2"
                  variant="header4"
                  className="modal-header__pro-signup__guiding-text"
                  data-qa-id="loginModalSignInHeaderText"
                >
                  Sign in to get Evite Pro
                </Typography>
                <Typography
                  as="p"
                  variant={isLargeAndUp ? 'paragraph2' : 'paragraph3'}
                  className="modal-header__pro-signup__guiding-text"
                  data-qa-id="loginModalSignInGuidingText"
                >
                  You’re seconds from unlocking all Pro has to offer.
                </Typography>
              </div>
            )}
            <div className="social-login-group">
              <Button
                variant="secondary"
                size="large"
                onClick={onGoogleLoginClick}
                id="google"
                data-qa-id="loginModalSocialGoogleLoginButton"
              >
                <span />
                Continue with Google
              </Button>
              <Button
                variant="secondary"
                size="large"
                onClick={onAppleLoginClick}
                id="apple"
                data-qa-id="loginModalSocialAppleLoginButton"
              >
                <span />
                Continue with Apple
              </Button>
            </div>
          </>
        ) : (
          <Typography as="p" variant="paragraph1" data-qa-id="forgotPasswordGuidingText">
            Enter your email address below and we will send you an email to reset your password.
          </Typography>
        )}
        <form
          className="login-form"
          onSubmit={forgotPasswordMode ? handleForgotPassword : handleLogin}
        >
          {!forgotPasswordMode && (
            <Typography as="label" variant="label2" size="small" data-qa-id="orSignInWithEmailText">
              Or Sign In with Email
            </Typography>
          )}
          <InputText
            id="login_modal_email_input"
            value={email}
            type="email"
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email address"
            error={forgotPasswordMode && emailError}
            data-qa-id="loginModalEmailInput"
            autoComplete="email"
          />
          {!forgotPasswordMode && (
            <InputText
              value={password}
              type="password"
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              error={loginFailure ? 'Invalid email or password. Try again.' : ''}
              data-qa-id="loginModalPasswordInput"
              autoComplete="password"
            />
          )}
          {forgotPasswordMode ? (
            <div className="forgot-password__cta-group">
              <Button
                variant="primary"
                size="large"
                type="submit"
                data-qa-id="sendResetEmailButton"
              >
                Send
              </Button>
              <Button
                variant="secondary"
                size="large"
                onClick={(e) => {
                  setEmailError('');
                  onForgotPasswordCancel(e);
                }}
                data-qa-id="cancelForgotPasswordButton"
              >
                Cancel
              </Button>
            </div>
          ) : (
            <>
              <Button
                variant="primary"
                size="large"
                type="submit"
                data-qa-id="loginModalSignInButton"
              >
                Sign in
              </Button>
              <div className="forgot-password-wrapper">
                <button
                  type="button"
                  onClick={(e) => {
                    setEmailError('');
                    onForgotPasswordClick(e);
                  }}
                  className="unstyled-button"
                  data-qa-id="loginModalForgotPasswordButton"
                >
                  <Typography
                    as="p"
                    variant="link"
                    size="large"
                    data-qa-id="loginModalForgotPasswordButtonText"
                  >
                    Forgot password?
                  </Typography>
                </button>
              </div>
            </>
          )}
        </form>
        {!forgotPasswordMode && (
          <Typography
            as="p"
            variant="paragraph2"
            className="sign-in-redirect"
            data-qa-id="loginModalDontHaveAccountText"
          >
            Don&apos;t have an account?{' '}
            <button
              type="button"
              className="unstyled-button link"
              onClick={onCreateAccountClick}
              data-qa-id="loginModalGoToCreateAccountButton"
            >
              Create one
            </button>
          </Typography>
        )}
        {forgotPasswordMode && (
          <Overlay open={forgotPasswordSentModalOpen} onClose={hideForgotPasswordSentModal}>
            <div className="forgot-password__sent-modal">
              <div className="forgot-password__sent-modal__header">
                <Typography as="h2" variant="header2" data-qa-id="forgotPasswordSentHeaderText">
                  Forgot Your Password?
                </Typography>
              </div>
              <Typography as="p" variant="paragraph2" data-qa-id="resetEmailSentGuidingText">
                If an account matching <span style={{color: tanzanite}}>{email}</span> is found, you
                should receive an email with instructions for resetting your password shortly.
              </Typography>
              <Button
                id="closeForgotPasswordSentModalBtn"
                variant="primary"
                size="large"
                onClick={hideForgotPasswordSentModal}
                data-qa-id="hideForgotPasswordSentModalButton"
              >
                Close
              </Button>
            </div>
          </Overlay>
        )}
      </div>
    </div>
  );
};

LoginContainer.propTypes = {
  onCloseModal: PropTypes.func,
  onSuccess: PropTypes.func,
  onForgotPasswordClick: PropTypes.func,
  onForgotPasswordCancel: PropTypes.func,
  onCreateAccountClick: PropTypes.func,
  onGoogleLoginClick: PropTypes.func,
  onAppleLoginClick: PropTypes.func,
  forgotPasswordMode: PropTypes.bool,
};
